import {
  type AssetRelationType,
  type Ownership,
  type ParsedAsset,
  type RealtorProfile,
} from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { uniqBy } from 'lodash-es'
import { useMemo, useRef, useState, type ReactNode } from 'react'
import { ActivityIndicator, Modal, Pressable, RefreshControl, View } from 'react-native'
import type Animated from 'react-native-reanimated'
import { ActionButton, type Action } from '~/components/Actions.tsx'
import LoadingAsset from '~/components/assets/LoadingAsset.tsx'
import PropertyCard from '~/components/assets/PropertyCard'
import { PropertyLeads } from '~/components/assets/PropertyLeads'
import { PropertyListItem } from '~/components/assets/PropertyListItem'
import { PropertyPriceAction } from '~/components/assets/PropertyPriceAction'
import { collectAssetDetails } from '~/components/assets/propertyUtils'
import { ProspectNotesDisplay } from '~/components/assets/ProspectNotesDisplay'
import {
  useDeleteProperty,
  useUpdatePropertyFromUrl,
} from '~/components/assets/usePropertyActions.tsx'
import { useAlert, type AlertAction } from '~/components/GlobalAlert.tsx'
import Icon from '~/components/Icon.tsx'
import ContentBox, { BoxActionsHeader } from '~/components/layout/ContentBox.tsx'
import Image from '~/components/layout/Image.tsx'
import { ValueList, type ValueItem } from '~/components/layout/ValueList'
import { ScrollView } from '~/components/ScrollView'
import { DisplayValue, Label } from '~/components/typography/Content.tsx'
import { Heading2, Heading3 } from '~/components/typography/Titles.tsx'
import { Badge } from '~/components/ui/badge'
import { Button, ButtonLabel } from '~/components/ui/button.tsx'
import {
  Collapsible,
  CollapsibleContent,
  CollapsiblePreview,
  CollapsibleTrigger,
} from '~/components/ui/collapsible'
import { TabBar, TabContent, Tabs, type TabItem } from '~/components/ui/tabs'
import { Link, Text } from '~/components/ui/text'
import { PropertyTypeBadge } from '~/components/ui/TypeBadge'
import { useAssetDetails } from '~/data/assets/useAssetDetails.ts'
import { usePendingAsset } from '~/data/assets/usePendingAsset.ts'
import { useAssetEventContact } from '~/data/contacts/useContacts'
import designTokens from '~/lib/designTokens.ts'
import { cn } from '~/lib/utils.ts'

export function PropertyView({ assetId, className }: { assetId: string; className?: string }) {
  const { data, isLoading, refetch, isRefetching } = useAssetDetails(assetId)
  const items = useMemo(() => data?.items ?? [], [data?.items])

  const relations = useMemo(
    () =>
      uniqBy(
        items.flatMap((item) => item.relations),
        'user_id'
      ),
    [items]
  )

  const relationType = data?.relationType

  const selectedItem = useMemo(() => {
    if (items.length === 0) {
      return undefined
    }

    return items.find((item) => item.id === assetId)
  }, [items, assetId])

  const { handleDelete, isDeleting } = useDeleteProperty(assetId)

  if (isLoading || !selectedItem) {
    return (
      <PropertyViewLoading className={className} onRefresh={refetch} isRefreshing={isRefetching} />
    )
  }

  const isRepresentation = relationType === 'representation'

  if (isRepresentation) {
    return (
      <RepresentationAssetView
        assetId={assetId}
        className={className}
        item={selectedItem}
        onRefresh={refetch}
        isRefreshing={isRefetching}
      />
    )
  }

  const hasMultipleRealtors = items.length > 1
  const selectedRelation = selectedItem.relations[0]
  const currentRealtor = selectedRelation?.realtor
    ? {
        assetId: selectedItem.id,
        ...selectedRelation.realtor,
      }
    : null

  return (
    <PropertyDetails
      asset={selectedItem}
      className={className}
      onRefresh={refetch}
      isRefreshing={isRefetching}
      onDelete={handleDelete}
      isLoading={isDeleting}
      realtorInfo={
        hasMultipleRealtors
          ? {
              currentRealtor: currentRealtor ?? null,
              allRealtors: relations
                .filter((item) => !!item.asset_id)
                .map((item) => ({
                  id: item.id,
                  userId: item.user_id,
                  realtor: item.realtor ?? null,
                  isOwn: item.is_own ?? false,
                  assetId: item.asset_id as string,
                })),
            }
          : currentRealtor
            ? { currentRealtor }
            : undefined
      }
    />
  )
}

function RepresentationAssetView({
  assetId,
  className,
  item,
  onRefresh,
  isRefreshing,
}: {
  assetId: string
  className?: string
  item: ParsedAsset
  onRefresh: () => void
  isRefreshing: boolean
}) {
  const { t } = useTranslations(['property', 'common'])
  const { showAlert } = useAlert()
  const { handleDelete, isDeleting } = useDeleteProperty(assetId)

  const {
    asset,
    isDataFetching,
    isMutationPending,
    retryExtraction,
    refetchAsset,
    isAssetComplete,
    taskStatus,
  } = usePendingAsset({
    assetId,
    onInitialFailure: (asset) => {
      const hasSource = asset && (asset.source.sourceWebUrl || asset.source.sourceDocumentUrl)

      showAlert({
        type: 'error',
        title: t(($) => $.errors.error),
        description: !hasSource
          ? t(($) => $.errors.extractionFailedNoSource)
          : t(($) => $.errors.extractionFailedRetry),
        actions: [
          hasSource && {
            label: t(($) => $.actions.retry),
            buttonVariant: 'primary',
            buttonSize: 'lg',
            onPress: () => retryExtraction(),
          },
          {
            label: t(($) => $.view.deleteButton),
            buttonVariant: hasSource ? 'secondary' : 'primary',
            buttonSize: 'lg',
            onPress: () => handleDelete(),
          },
        ].filter(Boolean) as AlertAction[],
        cancelButton: {
          label: t(($) => $.actions.close),
          buttonVariant: 'ghost',
          buttonSize: 'lg',
        },
      })
    },
  })

  const {
    handleTriggerUpdate,
    isPending: isUpdatePending,
    dialog,
  } = useUpdatePropertyFromUrl(assetId)

  const tasksPending = taskStatus === 'PENDING' || isUpdatePending || isDeleting

  const handleRefresh = async () => {
    await refetchAsset()
    onRefresh()
  }

  if (!asset) {
    return (
      <PropertyViewLoading
        className={className}
        onRefresh={handleRefresh}
        isRefreshing={isDataFetching || isRefreshing}
      />
    )
  }

  const hasSources = !!(asset?.source?.sourceDocumentUrl || asset?.source?.sourceWebUrl)

  if (tasksPending && !asset.data) {
    return (
      <PropertyViewLoading
        className={className}
        onRefresh={handleRefresh}
        isRefreshing={isDataFetching || isRefreshing}
        message={t(($) => $.errors.updatingPropertyData)}
      />
    )
  }

  if (!isAssetComplete && taskStatus === 'FAILED') {
    return (
      <ExtractionFailedView
        className={className}
        hasSources={hasSources}
        onRetry={retryExtraction}
        onDelete={handleDelete}
        isRetrying={isMutationPending}
        isDeleting={isDeleting}
      />
    )
  }

  return (
    <>
      <PropertyDetails
        asset={asset}
        className={className}
        isLoading={tasksPending}
        onRefresh={handleRefresh}
        isRefreshing={tasksPending ? undefined : isDataFetching || isRefreshing}
        onDelete={handleDelete}
        onUpdateFromUrl={asset.data && asset.source.sourceWebUrl ? handleTriggerUpdate : undefined}
        realtorInfo={
          item.relations[0]?.realtor
            ? { currentRealtor: { assetId, ...item.relations[0].realtor } }
            : undefined
        }
      />
      {dialog}
    </>
  )
}

type RealtorInfo =
  | {
      currentRealtor: ({ assetId: string } & RealtorProfile) | null
    }
  | {
      currentRealtor: ({ assetId: string } & RealtorProfile) | null
      allRealtors: {
        id: string
        realtor: RealtorProfile | null
        isOwn: boolean
        assetId: string
      }[]
    }

type PropertyViewTabs = 'events' | 'leads'

function PropertyDetails({
  asset,
  className,
  isLoading,
  onRefresh,
  isRefreshing,
  onDelete,
  onUpdateFromUrl,
  realtorInfo,
}: {
  asset: ParsedAsset
  className?: string
  isLoading?: boolean
  onRefresh?: () => void
  isRefreshing?: boolean
  onDelete?: () => void
  onUpdateFromUrl?: () => void
  realtorInfo?: RealtorInfo
}) {
  const { t } = useTranslations(['property', 'common'])
  const [isRealtorSelectorOpen, setIsRealtorSelectorOpen] = useState(false)
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(true)
  const [activeTab, setActiveTab] = useState<PropertyViewTabs>('leads')

  const relation = asset.relations?.[0]
  const relationType = relation?.relation_type
  const isProspect = relationType === 'prospect'
  const isReference = relationType === 'reference'
  const isOwnAsset = relation?.is_own

  const { data: appraisalContact } = useAssetEventContact(
    isProspect ? asset.id : undefined,
    'appraisal'
  )

  const { addressTitle, askingPrice } = useMemo(() => collectAssetDetails(asset), [asset])

  const hasMultipleRealtors =
    realtorInfo && 'allRealtors' in realtorInfo && realtorInfo.allRealtors.length > 1

  const headerActions = useMemo((): Action[] => {
    if (isReference || !isOwnAsset) {
      return []
    }

    const actions: Action[] = []

    actions.push({
      key: 'edit-property',
      label: t(($) => $.actions.edit),
      icon: 'home-edit-outline',
      href: {
        pathname: '/property/[assetId]/edit',
        params: { assetId: asset.id },
      },
    })

    if (!isReference && !isProspect) {
      actions.push({
        key: 'convert-to-reference',
        label: t(($) => $.actions.archive),
        icon: 'archive-outline',
        href: {
          pathname: '/property/[assetId]/convert',
          params: { assetId: asset.id },
        },
      })
    }

    return actions
  }, [asset.id, isProspect, isReference, isOwnAsset, t])

  const contextMenuActions = useMemo((): Action[] => {
    if (!isOwnAsset) {
      return []
    }

    const actions: Action[] = []

    if (onUpdateFromUrl) {
      actions.push({
        key: 'update-url',
        label: t(($) => $.actions.updateFromUrl),
        description: t(($) => $.view.editDescription),
        icon: 'refresh-cw',
        iconLibrary: 'materialcommunityicons',
        onPress: onUpdateFromUrl,
      })
    }

    if (onDelete) {
      actions.push({
        key: 'delete-property',
        label: t(($) => $.actions.delete),
        description: t(($) => $.view.deleteDescription),
        icon: 'trash-2',
        iconLibrary: 'feather',
        labelClassName: 'text-destructive group-hover/button:text-destructive-foreground',
        iconClassName: 'text-destructive group-hover/button:text-destructive-foreground',
        onPress: onDelete,
      })
    }

    return actions
  }, [isOwnAsset, onUpdateFromUrl, onDelete, t])

  const tabs = useMemo(
    () =>
      [
        {
          id: 'leads',
          label: t(($) => $.leads.title),
          animationOrigin: 'top-left',
          content: <PropertyLeads assetId={asset.id} hasPrice={!!askingPrice} showTitleCount />,
        },
      ].filter(Boolean) as TabItem<PropertyViewTabs>[],
    [asset.id, askingPrice, t]
  )

  const tabContentRef = useRef<View>(null)
  const scrollRef = useRef<Animated.ScrollView>(null)

  const contactValues = useMemo(() => {
    const values: ValueItem[] = []

    if (appraisalContact?.name) {
      values.push({
        icon: 'person-outline',
        value: t(($) => $.view.contactPerson),
        description: appraisalContact.name,
      })
    }

    if (appraisalContact?.phone) {
      values.push({
        icon: 'call-outline',
        value: t(($) => $.view.phone),
        description: appraisalContact.phone,
      })
    }

    if (appraisalContact?.email) {
      values.push({
        icon: 'mail-outline',
        value: t(($) => $.view.email),
        description: appraisalContact.email,
      })
    }

    return values
  }, [appraisalContact, t])

  return (
    <Tabs<PropertyViewTabs> tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab}>
      <ScrollView
        ref={scrollRef}
        className={cn(
          'flex-1 self-stretch rounded-content transition-opacity duration-150',
          className
        )}
        offsetTrackHeight
        contentContainerClassName="gap-3"
        refreshControl={
          onRefresh ? (
            <RefreshControl refreshing={isRefreshing ?? false} onRefresh={onRefresh} />
          ) : undefined
        }>
        <ContentBox
          scrollable={false}
          contentContainerClassName="gap-4 p-0"
          header={
            <BoxActionsHeader
              className={cn(
                'relative mb-4',
                (headerActions.length === 0 || isReference) && 'rounded-b-none'
              )}
              toolbarClassName="justify-evenly"
              contentClassName="gap-4 pb-4"
              contextMenuActions={contextMenuActions.length > 0 ? contextMenuActions : undefined}
              actions={headerActions.length > 0 ? headerActions : undefined}>
              <PropertyViewHeader
                relationType={relationType}
                ownership={isOwnAsset ? 'own' : 'organisation'}
                organisationName={asset.organisation?.name}
                realtorName={asset.organisation?.realtorName}
                title={addressTitle}
                isLoading={isLoading}
              />
              {realtorInfo?.currentRealtor && (hasMultipleRealtors || !isOwnAsset) ? (
                <Pressable
                  onPress={() => setIsRealtorSelectorOpen(true)}
                  disabled={!hasMultipleRealtors}
                  className={cn(!hasMultipleRealtors && 'cursor-default')}>
                  <RealtorCardDisplay
                    realtor={realtorInfo.currentRealtor}
                    isOwn={
                      hasMultipleRealtors
                        ? (realtorInfo.allRealtors.find(
                            (item) => item.realtor?.name === realtorInfo.currentRealtor?.name
                          )?.isOwn ?? false)
                        : false
                    }
                    showExpandIcon={hasMultipleRealtors}
                  />
                </Pressable>
              ) : null}
            </BoxActionsHeader>
          }>
          <Collapsible
            isOpen={!isDetailsCollapsed}
            onOpenChange={(open) => setIsDetailsCollapsed(!open)}>
            <CollapsibleTrigger
              className="gap-2 pb-2"
              labelClassName="text-center text-sm underline-offset-2">
              {isDetailsCollapsed
                ? t(($) => $.view.expandDetails)
                : t(($) => $.view.collapseDetails)}
            </CollapsibleTrigger>
            <CollapsiblePreview height={250} className="px-2">
              <View className="gap-3 pb-4">
                <PropertyListItem
                  asset={asset}
                  isPressable={false}
                  showTypeBadge={false}
                  className="mx-2 flex-1 shadow-none"
                />
                {!isReference && isOwnAsset && !askingPrice ? (
                  <View className="items-center">
                    <PropertyPriceAction
                      asset={asset}
                      onSuccess={onRefresh}
                      triggerClassName="mx-auto"
                    />
                  </View>
                ) : null}
              </View>
            </CollapsiblePreview>
            <CollapsibleContent className="mt-2">
              <PropertyCard
                asset={asset}
                display="inline"
                className="flex-initial"
                showAddPrice={!isReference && isOwnAsset}
                onPriceAdded={onRefresh}
              />
              {isProspect && <ProspectNotesDisplay asset={asset} className="mb-4" />}
              {isOwnAsset && !isReference && (
                <ActionButton
                  className="mx-3 mb-8"
                  action={{
                    key: 'edit-property',
                    label: t(($) => $.actions.editDetails),
                    actionType: 'set',
                    buttonSize: 'lg',
                    buttonVariant: 'outline',
                    icon: 'home-edit-outline',
                    iconSize: 24,
                    href: `/property/${asset.id}/edit`,
                  }}
                />
              )}
            </CollapsibleContent>
          </Collapsible>
          {isProspect && contactValues.length > 0 && <ValueList values={contactValues} />}
        </ContentBox>
        {tabs.length > 1 ? (
          <View className="flex-1">
            <TabBar className="gap-1.5 border-0 bg-transparent" />
            <TabContent ref={tabContentRef} />
          </View>
        ) : (
          (tabs[0]?.content as ReactNode)
        )}
      </ScrollView>

      {hasMultipleRealtors ? (
        <RealtorSelectionModal
          isOpen={isRealtorSelectorOpen}
          onClose={() => setIsRealtorSelectorOpen(false)}
          realtors={realtorInfo.allRealtors}
          selectedRealtorId={asset.id}
        />
      ) : null}
    </Tabs>
  )
}

function PropertyViewLoading({
  className,
  onRefresh,
  isRefreshing,
  message,
}: {
  className?: string
  onRefresh?: () => void
  isRefreshing?: boolean
  message?: string
}) {
  return (
    <ContentBox
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
      className={className}
      contentContainerClassName="flex-1 pt-0">
      {message ? (
        <View className="flex-1 gap-4 px-6">
          <Text className="text-center">{message}</Text>
          <LoadingAsset />
        </View>
      ) : (
        <LoadingAsset />
      )}
    </ContentBox>
  )
}

function ExtractionFailedView({
  className,
  hasSources,
  onRetry,
  onDelete,
  isRetrying,
  isDeleting,
}: {
  className?: string
  hasSources: boolean
  onRetry: () => void
  onDelete: () => void
  isRetrying: boolean
  isDeleting: boolean
}) {
  const { t } = useTranslations('property')

  return (
    <ContentBox className={className} contentContainerClassName="flex-1 pt-0">
      <View className="my-6 flex-1 gap-6 p-4">
        <Label className="text-center">
          {t(($) => $.view.extractionFailedTitle)}
          {hasSources ? ` ${t(($) => $.actions.retry)}.` : ''}
        </Label>
        <View className="flex-col items-center gap-6">
          {hasSources ? (
            <Button
              variant="primary"
              size="lg"
              onPress={onRetry}
              loading={isRetrying}
              disabled={isRetrying}>
              {t(($) => $.view.extractionFailedRetry)}
            </Button>
          ) : (
            <DisplayValue className="text-center">
              {t(($) => $.view.extractionFailedDelete)}
            </DisplayValue>
          )}
          <Button
            variant={hasSources ? 'ghost' : 'primary'}
            size={hasSources ? 'sm' : 'lg'}
            onPress={onDelete}
            loading={isDeleting}
            disabled={isDeleting}>
            <ButtonLabel className={cn(hasSources && 'text-neutral-500')}>
              {t(($) => $.view.deleteButton)}
            </ButtonLabel>
          </Button>
        </View>
      </View>
    </ContentBox>
  )
}

function PropertyViewHeader({
  title,
  relationType,
  ownership,
  organisationName,
  realtorName,
  isLoading,
}: {
  title: string
  relationType?: AssetRelationType
  ownership?: Ownership
  organisationName?: string | null
  realtorName?: string | null
  isLoading?: boolean
}) {
  const { t } = useTranslations('property')

  return (
    <View className="w-full items-center gap-2">
      <Heading2 className="text-center">{title}</Heading2>
      <View className="flex-row items-center gap-2">
        <PropertyTypeBadge
          type={relationType}
          ownership={ownership}
          organisationName={organisationName}
          realtorName={realtorName}
        />
      </View>
      {isLoading ? (
        <View className="flex-row items-center justify-center gap-4">
          <ActivityIndicator size="small" color={designTokens.secondary} />
          <Text className="text-secondary">{t(($) => $.errors.updatingPropertyData)}</Text>
        </View>
      ) : null}
    </View>
  )
}

function RealtorCardDisplay({
  realtor,
  isOwn,
  showExpandIcon,
  className,
}: {
  realtor: RealtorProfile
  isOwn: boolean
  showExpandIcon?: boolean
  className?: string
}) {
  const { t } = useTranslations('property')
  const avatarUrl = realtor?.data?.avatar_url

  return (
    <View
      className={cn(
        'flex-row items-center gap-3 rounded-xl border-2 border-muted bg-card p-3',
        className
      )}>
      <View className="h-10 w-10 items-center justify-center overflow-hidden rounded-full bg-muted">
        {avatarUrl ? (
          <Image source={{ uri: avatarUrl }} style={{ width: 40, height: 40 }} contentFit="cover" />
        ) : (
          <Icon name="person-outline" size={20} className="text-secondary" />
        )}
      </View>
      <View className="flex-1">
        <Text className="font-medium">{realtor?.name ?? t(($) => $.view.unknownRealtor)}</Text>
        {realtor?.data?.company ? (
          <Text className="text-sm text-secondary">{realtor.data.company}</Text>
        ) : null}
      </View>
      {isOwn ? (
        <Badge variant="outline" size="xs" textClassName="font-normal text-xxs uppercase">
          {t(($) => $.view.you)}
        </Badge>
      ) : null}
      {showExpandIcon ? <Icon name="chevron-down" size={20} className="text-primary" /> : null}
    </View>
  )
}

function RealtorSelectionModal({
  isOpen,
  onClose,
  realtors,
  selectedRealtorId,
}: {
  isOpen: boolean
  onClose: () => void
  realtors: {
    id: string
    realtor: RealtorProfile | null
    isOwn: boolean
    assetId: string
  }[]
  selectedRealtorId?: string
}) {
  const { t } = useTranslations('property')

  return (
    <Modal visible={isOpen} transparent animationType="fade" onRequestClose={onClose}>
      <Pressable className="flex-1 items-center justify-center bg-black/50" onPress={onClose}>
        <View className="m-4 max-h-[80%] w-[90%] max-w-md rounded-2xl bg-background">
          <View className="border-b border-border p-4">
            <Heading3>{t(($) => $.view.selectRealtor)}</Heading3>
          </View>
          <ScrollView className="p-4" contentContainerClassName="gap-3">
            {realtors.map((item) =>
              item.realtor ? (
                <Link
                  key={item.id}
                  href={{
                    pathname: `/property/[assetId]`,
                    params: { assetId: item.assetId },
                  }}
                  asChild>
                  <Pressable className="relative flex flex-row items-center">
                    <RealtorCardDisplay
                      realtor={item.realtor}
                      isOwn={item.isOwn}
                      showExpandIcon={false}
                      className={cn(
                        'flex-1',
                        selectedRealtorId === item.assetId &&
                          cn('border-muted bg-muted', item.isOwn && 'pr-9')
                      )}
                    />
                    {selectedRealtorId === item.assetId && (
                      <Icon
                        name="checkmark-circle"
                        size={20}
                        className="absolute right-2 text-accent"
                      />
                    )}
                  </Pressable>
                </Link>
              ) : null
            )}
          </ScrollView>
          <View className="border-t border-border p-4">
            <Button variant="ghost" onPress={onClose}>
              {t(($) => $.actions.close)}
            </Button>
          </View>
        </View>
      </Pressable>
    </Modal>
  )
}
